import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PageWrapper from './PageWrapper';
import EditableText from './EditableText';
import { selectPageSlice } from '../Selector';
import { reportPageCount } from '../../../redux/actions';

// Must mirror the page geometry used by the other paginating sections.
const PAGE_HEIGHT = 1123;
const PADDING_TOP = 64;
const PADDING_BOTTOM = 60;
const FOOTER_HEIGHT = 40;
const CONTENT_HEIGHT = PAGE_HEIGHT - PADDING_TOP - PADDING_BOTTOM - FOOTER_HEIGHT; // 959px
const CONTENT_WIDTH = 794 - 60 - 60; // 674px

// Small tail safety so a row landing exactly on the page boundary (sub-pixel
// rounding between measure and render) is pushed to the next page rather than
// clipped. All real spacing (table margins, block gap, borders, header margin
// collapse) is now measured explicitly below, so this only guards rounding.
const PAGE_TAIL_BUFFER = 6;

// The two Key Trends tables (gains, declines) share this header. `<thead>` is
// repeated on every page group so a table that splits across pages still shows
// its column headers — same behaviour as the priority tables.
// Under `table-layout: fixed` the browser derives column widths from the FIRST
// row's cells — but the first header row is Lever / Behaviors / (All But Self
// colSpan 2), so the Score/Change widths on the second row are ignored. A
// <colgroup> is the authoritative width source and isn't affected by
// rowSpan/colSpan, so the four columns size exactly as specified.
function KeyTrendsColgroup() {
  return (
    <colgroup>
      <col style={{ width: '120px' }} />
      <col />{/* Behaviors: takes the remaining width */}
      <col style={{ width: '80px' }} />
      <col style={{ width: '80px' }} />
    </colgroup>
  );
}

function KeyTrendsThead() {
  return (
    <thead>
      <tr>
        <th className="key-trends-col-lever" rowSpan={2}>PILLAR</th>
        <th className="key-trends-col-behavior" rowSpan={2}>Behaviors</th>
        <th className="key-trends-col-allbutself" colSpan={2}>All But Self</th>
      </tr>
      <tr>
        <th className="key-trends-col-score">Score</th>
        <th className="key-trends-col-change">Change in Score</th>
      </tr>
    </thead>
  );
}

function KeyTrendsRow({ row }) {
  return (
    <tr>
      <td className="key-trends-col-lever">{row[0]}</td>
      <td className="key-trends-col-behavior">{row[1]}</td>
      <td className="key-trends-col-score">{row[2]}</td>
      <td className="key-trends-col-change">{row[3]}</td>
    </tr>
  );
}

// A "block" is one table on the page: a label (editable) followed by its rows.
// The paginator measures the label, the thead and each row, then distributes
// them across pages, splitting a table's rows and reprinting the thead + label
// as needed so nothing is ever clipped by the page's overflow:hidden.
function buildBlocks(data) {
  return [
    {
      key: 'gains',
      labelPath: 'trendGainLoss.gainsLabel',
      labelFallback: 'Top 3 gains in perception',
      rows: data.gainTableData || [],
    },
    {
      key: 'losses',
      labelPath: 'trendGainLoss.lossesLabel',
      labelFallback: 'Top 3 declines in perception',
      rows: data.lossTableData || [],
    },
  ];
}

function KeyTrendsPage({ data, personName, startPageNumber, slice, reportPageCount }) {
  const blocks = buildBlocks(data);

  const measureRef = useRef(null);
  const [pages, setPages] = useState(null);

  // Re-measure whenever the editable slice changes (label/copy edits).
  const isFirstSliceRef = useRef(true);
  useEffect(() => {
    if (isFirstSliceRef.current) {
      isFirstSliceRef.current = false;
      return;
    }
    setPages(null);
  }, [slice]);

  useLayoutEffect(() => {
    if (pages) return;
    if (!measureRef.current) return;
    const container = measureRef.current;

    const marginY = (el) => {
      if (!el) return 0;
      const cs = window.getComputedStyle(el);
      return (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
    };
    const rectH = (el) => (el ? el.getBoundingClientRect().height : 0);
    const containerTop = container.getBoundingClientRect().top;
    // True vertical space an element occupies from the container top, INCLUDING
    // margins that collapse out of its own bounding rect. Measured off the
    // element's offset within the (margin-collapse-free) measurement container.
    const occupiedTop = (el) => (el ? el.getBoundingClientRect().top - containerTop : 0);

    // The header's own rect excludes its last child's bottom margin (it collapses
    // out), so measure the header as the distance from the container top to the
    // first block's top — the real space the header consumes on page 1.
    const headerEl = container.querySelector('[data-kt="header"]');
    const firstBlockEl = container.querySelector('[data-kt-block="0"]');
    const headerH = firstBlockEl
      ? occupiedTop(firstBlockEl) - occupiedTop(headerEl)
      : rectH(headerEl) + marginY(headerEl);

    // Distribute blocks (and their rows) across pages. Each output page is a
    // list of "segments": { blockIdx, label(bool), rowIdxs[] }. `label` is true
    // only for the segment that starts a block; a block split across pages omits
    // the label (but repeats the thead) on continuation pages.
    const outPages = [];
    let curPage = [];
    // `curUsed` tracks CONTENT height placed on the current page only. The page-1
    // header is NOT added here — it's subtracted from the page-1 budget in
    // avail() instead. (The earlier bug counted the header in BOTH places, which
    // shrank the usable space to near-zero and split the table after ~1 row even
    // with most of the page empty.)
    let curUsed = 0;
    let isFirstPage = true;

    const avail = () =>
      (isFirstPage ? CONTENT_HEIGHT - headerH : CONTENT_HEIGHT) - PAGE_TAIL_BUFFER;

    blocks.forEach((block, blockIdx) => {
      const blockEl = container.querySelector(`[data-kt-block="${blockIdx}"]`);
      const labelEl = container.querySelector(`[data-kt-label="${blockIdx}"]`);
      const theadEl = container.querySelector(`[data-kt-thead="${blockIdx}"]`);
      const tableEl = container.querySelector(`[data-kt-table="${blockIdx}"]`);
      const rowEls = Array.from(container.querySelectorAll(`[data-kt-row^="${blockIdx}-"]`));
      const rowHs = rowEls.map(rectH);

      // Everything above the FIRST row that this block adds to the page:
      //   block margin-bottom (gap to the next block, counted when the block is
      //   NOT first on the page) + label (incl. its margins) + table top margin +
      //   thead + any border overhead. Measured off the real DOM (margin-inclusive)
      //   so nothing is under-counted and the last row can't be clipped.
      const theadH = rectH(theadEl);
      const labelOuter = rectH(labelEl) + marginY(labelEl);
      const tableMarginTop = tableEl ? parseFloat(window.getComputedStyle(tableEl).marginTop) || 0 : 0;
      // Border/spacing the browser adds beyond thead + summed rows.
      const sumRowH = rowHs.reduce((s, h) => s + h, 0);
      const tableBorderOverhead = Math.max(0, rectH(tableEl) - theadH - sumRowH);
      // Gap to the previous block on the same page (the block wrapper margin).
      const blockGap = curPage.length > 0 ? marginY(blockEl) : 0;

      let seg = { blockIdx, label: true, rowIdxs: [] };
      // Chrome the segment needs before any rows.
      const chromeNoGap = labelOuter + tableMarginTop + theadH + tableBorderOverhead;
      let segChrome = blockGap + chromeNoGap;
      // Ensure the chrome + at least one row can start on the current page.
      if (curUsed + segChrome + (rowHs[0] || 0) > avail() && curPage.length > 0) {
        outPages.push(curPage);
        curPage = [];
        isFirstPage = false;
        curUsed = 0;
        segChrome = chromeNoGap; // fresh page: no preceding block gap
      }
      curUsed += segChrome;

      block.rows.forEach((_, rIdx) => {
        const h = rowHs[rIdx] || 0;
        if (curUsed + h > avail() && seg.rowIdxs.length > 0) {
          // Close this segment on the current page, continue the block on a new
          // page with a fresh thead (label omitted — it's a continuation).
          curPage.push(seg);
          outPages.push(curPage);
          curPage = [];
          isFirstPage = false;
          seg = { blockIdx, label: false, rowIdxs: [] };
          curUsed = tableMarginTop + theadH + tableBorderOverhead; // continuation repeats the thead
        }
        seg.rowIdxs.push(rIdx);
        curUsed += h;
      });

      curPage.push(seg);
    });

    if (curPage.length > 0) outPages.push(curPage);
    const groups = outPages.length > 0 ? outPages : [[]];
    setPages(groups);

    if (reportPageCount) {
      reportPageCount('trendGainLoss', groups.length);
    }
  }, [pages, data, reportPageCount]);

  const header = (
    <>
      <h2 className="pdf-section-heading pdf-section-heading--numbered">
        <span className="pdf-section-heading__badge">6</span>
        <EditableText as="span" path="trendGainLoss.sectionHeading" variant="inline" stripInlineWrap fallback="KEY TRENDS" />
      </h2>
      <EditableText as="div" className="pdf-body-text" path="trendGainLoss.intro" variant="block" />
      <EditableText as="h3" className="pdf-sub-heading" path="trendGainLoss.definitionHeading" variant="inline" stripInlineWrap fallback="Definition of Gain / Decline in Perception (6-point scale):" />
      <EditableText as="div" className="pdf-bullet-list" path="trendGainLoss.definitionBullets" variant="block" />
      <EditableText as="div" className="pdf-body-text" path="trendGainLoss.notShownNote" variant="block" />
      <EditableText as="h3" className="pdf-sub-heading" path="trendGainLoss.useHeading" variant="inline" stripInlineWrap fallback="Use the key trends to identify:" />
      <EditableText as="div" className="pdf-bullet-list" path="trendGainLoss.useBullets" variant="block" />
      <EditableText as="div" className="pdf-body-text" path="trendGainLoss.closing" variant="block" />
    </>
  );

  const renderLabel = (block) => (
    <EditableText
      as="h3"
      className="pdf-section-heading pdf-section-heading--sub"
      path={block.labelPath}
      variant="inline"
      stripInlineWrap
      fallback={block.labelFallback}
    />
  );

  // ── Phase 1: measurement ────────────────────────────────────────────────────
  if (!pages) {
    return (
      <div style={{ position: 'relative', height: 0, overflow: 'hidden' }}>
        <div
          ref={measureRef}
          style={{ position: 'absolute', top: 0, left: 0, width: CONTENT_WIDTH, visibility: 'hidden', pointerEvents: 'none' }}
        >
          <div data-kt="header">{header}</div>
          {blocks.map((block, blockIdx) => (
            <div key={block.key} data-kt-block={blockIdx} className="key-trends-table">
              <div data-kt-label={blockIdx}>{renderLabel(block)}</div>
              <table data-kt-table={blockIdx} className="pdf-table pdf-table--key-trends">
                <KeyTrendsColgroup />
                <thead data-kt-thead={blockIdx}>
                  <tr>
                    <th className="key-trends-col-lever" rowSpan={2}>Lever</th>
                    <th className="key-trends-col-behavior" rowSpan={2}>Behaviors</th>
                    <th className="key-trends-col-allbutself" colSpan={2}>All But Self</th>
                  </tr>
                  <tr>
                    <th className="key-trends-col-score">Score</th>
                    <th className="key-trends-col-change">Change in Score</th>
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, rIdx) => (
                    <tr key={rIdx} data-kt-row={`${blockIdx}-${rIdx}`}>
                      <td className="key-trends-col-lever">{row[0]}</td>
                      <td className="key-trends-col-behavior">{row[1]}</td>
                      <td className="key-trends-col-score">{row[2]}</td>
                      <td className="key-trends-col-change">{row[3]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Phase 2: render pages ───────────────────────────────────────────────────
  return (
    <>
      {pages.map((segments, pageIdx) => (
        <PageWrapper key={pageIdx} pageNumber={startPageNumber + pageIdx} personName={personName}>
          {pageIdx === 0 && header}
          {segments.map((seg, segIdx) => {
            const block = blocks[seg.blockIdx];
            return (
              <div key={segIdx} className="key-trends-table">
                {seg.label && renderLabel(block)}
                <table className="pdf-table pdf-table--key-trends">
                  <KeyTrendsColgroup />
                  <KeyTrendsThead />
                  <tbody>
                    {seg.rowIdxs.map((rIdx) => (
                      <KeyTrendsRow key={rIdx} row={block.rows[rIdx]} />
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </PageWrapper>
      ))}
    </>
  );
}

const mapStateToProps = (state) => ({
  slice: selectPageSlice(state, 'trendGainLoss'),
});

const mapDispatchToProps = (dispatch) => ({
  reportPageCount: (sectionKey, count) => dispatch(reportPageCount(sectionKey, count)),
});

export default connect(mapStateToProps, mapDispatchToProps)(KeyTrendsPage);
